import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import {
  Layers,
  Feather,
  Hammer,
  Thermometer,
  Volume2,
  Wrench,
  Timer,
  Truck,
  ArrowUpRight,
  Phone,
  MapPin,
  Mail,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";

const CHAPE_IMG =
  "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/64dlk4nx_20260304_134950.jpg";
const CHAPE_ALLEGEE_IMG =
  "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/n1nd3e1w_copilot_image_1777624997018.jpeg";
const ISOLATION_IMG =
  "https://images.unsplash.com/photo-1774628012818-a256210b1373?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const BETON_IMG =
  "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/8scg9ah7_image_1777561239581.jpeg";
const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/dzj58mjp_Designer.png";

const RAMPE_IMG =
  "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/dt2ww3ta_Snapchat-1826855908.jpg";

const CHAPES = [
  { icon: Layers, title: "Chape traditionnelle", desc: "Le grand classique du bâtiment, taloché à la main pour un sol stable et durable.", to: "/chapes/chape-traditionnelle", image: CHAPE_IMG },
  { icon: Feather, title: "Chape allégée", desc: "Une chape légère, idéale pour la rénovation et les structures existantes.", to: "/chapes/chape-allegee", image: CHAPE_ALLEGEE_IMG },
  { icon: Hammer, title: "Béton dur", desc: "Sol industriel lissé à haute résistance pour ateliers, garages et entrepôts.", to: "/chapes/beton-dur", image: BETON_IMG },
  { icon: Hammer, title: "Rampe d'accès garage", desc: "Rampes béton rainurées antidérapantes pour l'accès aux garages et parkings.", to: "/chapes/rampe-acces", image: RAMPE_IMG },
];

const ISOLATIONS = [
  { icon: Thermometer, title: "Isolation thermique", desc: "Complexes isolants sous chape pour optimiser la performance énergétique.", to: "/isolations/isolation-thermique" },
  { icon: Volume2, title: "Isolation phonique", desc: "Sous-couches résilientes contre les bruits d'impact, conformes à la NRA.", to: "/isolations/isolation-phonique" },
];

const SOLUTIONS = [
  { icon: Wrench, title: "Rénovation des sols", desc: "Reprise et mise à niveau de planchers existants.", to: "/solutions/renovation-sols" },
  { icon: Timer, title: "Séchage rapide", desc: "Délais raccourcis pour livrer plus vite.", to: "/solutions/sechage-rapide" },
  { icon: Truck, title: "Sols à trafic intense", desc: "Ateliers, garages, entrepôts.", to: "/solutions/sols-trafic" },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      <SEO
        title="Chapiste à Mulhouse & Illzach — Chape, béton, isolation"
        description="Euro Chape, artisan chapiste à Illzach (68110) près de Mulhouse. Chape traditionnelle, chape allégée, béton dur, isolation thermique et phonique, parquet, résine moquette de pierre. Devis gratuit sous 48h en Alsace et Territoire de Belfort."
        keywords="chape Mulhouse, chapiste Mulhouse, chape Illzach, chape Alsace, chape traditionnelle, chape allégée, béton dur, isolation thermique, isolation phonique, chapiste Haut-Rhin"
        canonical="https://eurochape.com/"
      />
      {/* HERO — split layout, illustration à droite */}
      <section className="pt-28 md:pt-44 pb-20 md:pb-28 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <SectionLabel>Euro Chape · Illzach, Alsace</SectionLabel>
              <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[7rem] leading-[0.85] mt-8 text-[#1A2D7E]">
                Le sol,
                <br />
                la base de tout.
                <br />
                <span className="text-[#F08722]">Notre métier.</span>
              </h1>
              <p className="mt-10 max-w-2xl text-lg sm:text-xl font-light leading-relaxed text-[#1A2D7E]">
                Chape traditionnelle, béton dur, rampe d'accès garage, isolation thermique
                et phonique : EURO CHAPE prépare vos sols avec la rigueur d'un
                artisan. Implantée à Illzach, nous couvrons l'Alsace et le
                Territoire de Belfort, en neuf comme en rénovation.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/devis"
                  data-testid="hero-devis-cta"
                  className="inline-flex items-center gap-3 bg-[#F08722] text-white px-8 py-4 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#1A2D7E] transition-colors"
                >
                  Demander un devis <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-services-cta"
                  className="inline-flex items-center gap-3 border border-[#1A2D7E] text-[#1A2D7E] px-8 py-4 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#1A2D7E] hover:text-white transition-colors"
                >
                  Découvrir nos solutions
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Illustration / composition stacked icons */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-square max-w-md ml-auto">
              <div className="absolute inset-0 bg-[#1A2D7E]" />
              <div className="absolute inset-4 border border-[#F08722]" />
              <div className="absolute top-6 left-6 w-20 h-20 bg-[#F08722] flex items-center justify-center">
                <Layers className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
              <div className="absolute top-6 right-6 w-20 h-20 border-2 border-[#F08722] flex items-center justify-center">
                <Thermometer className="w-10 h-10 text-[#F08722]" strokeWidth={1.5} />
              </div>
              <div className="absolute bottom-6 left-6 w-20 h-20 border-2 border-white flex items-center justify-center">
                <Volume2 className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
              <div className="absolute bottom-6 right-6 w-20 h-20 bg-white flex items-center justify-center">
                <Hammer className="w-10 h-10 text-[#1A2D7E]" strokeWidth={1.5} />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="font-display font-black uppercase text-white text-center leading-[0.9]">
                  <div className="text-xs tracking-[0.3em] text-[#F08722] mb-2">Chape · Béton</div>
                  <div className="text-3xl tracking-tighter">Isolation</div>
                  <div className="text-3xl tracking-tighter">des sols</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#1A2D7E] text-white">
        <div className="px-6 md:px-12 lg:px-24 py-6 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12 text-center">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#F08722]">
            À Illzach
          </span>
          <span className="hidden md:inline-block w-px h-4 bg-white/30" />
          <span className="font-display font-bold text-base md:text-lg tracking-tight">
            Alsace · Territoire de Belfort · Haut-Rhin · Bas-Rhin
          </span>
        </div>
      </section>

      {/* INTRO */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionLabel>Notre savoir-faire</SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[0.98] text-[#1A2D7E]">
              Chaque millimètre compte.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
            <p className="text-lg sm:text-xl font-light leading-relaxed text-[#1A2D7E]">
              Depuis notre atelier d'Illzach, nous intervenons directement sur
              vos chantiers — maisons individuelles, rénovations, locaux
              professionnels, bâtiments industriels. Chaque projet est mené
              sans sous-traitance, avec des dosages et des matériaux maîtrisés
              de bout en bout.
            </p>
            <ul className="mt-10 space-y-4">
              {[
                "Déplacement en Alsace et Territoire de Belfort sous 48h",
                "Préparation du support et dosages contrôlés à chaque coulage",
                "Un seul interlocuteur, du devis à la réception du chantier",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-base text-[#1A2D7E]">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#F08722] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/a-propos"
              data-testid="home-about-link"
              className="mt-10 inline-flex items-center gap-3 link-underline text-xs uppercase tracking-[0.22em] font-semibold text-[#1A2D7E]"
            >
              En savoir plus <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NOS CHAPES — 3 cards corporate */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#EBEBE6] border-y border-[#D5D5D0]">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionLabel>Nos chapes</SectionLabel>
              <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#1A2D7E]">
                Quatre techniques,
                <br />
                <span className="text-[#F08722]">tous les chantiers.</span>
              </h2>
            </div>
            <Link
              to="/services#nos-chapes"
              data-testid="home-all-chapes"
              className="link-underline text-xs uppercase tracking-[0.22em] font-semibold inline-flex items-center gap-2 text-[#1A2D7E]"
            >
              Voir toutes nos chapes <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {CHAPES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <Link
                to={c.to}
                data-testid={`home-chape-${i}`}
                className="block bg-white border border-[#D5D5D0] hover:border-[#F08722] transition-colors group h-full"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-[0.22em] text-[#F08722] font-semibold">
                      Chape
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-[#1A2D7E] group-hover:text-[#F08722] transition-colors" />
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-2xl mt-3 text-[#1A2D7E]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm font-light text-[#5C5E58] leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NOS ISOLATIONS */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionLabel>Nos isolations</SectionLabel>
              <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#1A2D7E]">
                Performance
                <br />
                <span className="text-[#F08722]">thermique & acoustique.</span>
              </h2>
            </div>
            <Link
              to="/services#nos-isolations"
              data-testid="home-all-isolations"
              className="link-underline text-xs uppercase tracking-[0.22em] font-semibold inline-flex items-center gap-2 text-[#1A2D7E]"
            >
              Voir toutes nos isolations <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
          {ISOLATIONS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <Link
                to={c.to}
                data-testid={`home-isolation-${i}`}
                className="block bg-white border border-[#D5D5D0] hover:border-[#F08722] p-8 md:p-10 transition-colors group h-full"
              >
                <div className="flex items-start justify-between">
                  <c.icon className="w-10 h-10 text-[#F08722]" strokeWidth={1.5} />
                  <ArrowUpRight className="w-5 h-5 text-[#1A2D7E] group-hover:text-[#F08722] transition-colors" />
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#F08722] font-semibold mt-10">
                  Isolation
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl mt-3 text-[#1A2D7E]">
                  {c.title}
                </h3>
                <p className="mt-4 text-sm md:text-base font-light text-[#5C5E58] leading-relaxed">
                  {c.desc}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BÂTIR AVEC DES ÉLÉMENTS SÛRS */}
      <section className="grid grid-cols-1 lg:grid-cols-2 border-y border-[#D5D5D0]">
        <div className="relative min-h-[400px] lg:min-h-[600px] overflow-hidden">
          <img
            src={ABOUT_IMG}
            alt="Précision Euro Chape"
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-md"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A2D7E]/40 via-[#1A2D7E]/55 to-[#1A2D7E]/85" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span className="font-display font-black uppercase tracking-tighter text-[#1A2D7E] text-6xl sm:text-8xl lg:text-[9rem] leading-[0.82]">
              EURO
            </span>
            <span className="font-display font-black uppercase tracking-tighter text-[#F08722] text-6xl sm:text-8xl lg:text-[9rem] leading-[0.82] mt-2">
              CHAPE
            </span>
            <span className="mt-6 h-1 w-40 bg-[#F08722]" />
            <span className="mt-5 text-xs uppercase tracking-[0.3em] text-white/90 font-semibold">
              Illzach · Alsace
            </span>
          </div>
        </div>
        <div className="bg-[#1A2D7E] text-white px-6 md:px-12 lg:px-20 py-20 md:py-28 flex flex-col justify-center">
          <Reveal>
            <SectionLabel className="text-[#9A9A95]">
              <ShieldCheck className="w-4 h-4 text-[#F08722] inline-block mr-2" />
              L'exigence jusqu'au détail
            </SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-8 leading-[0.95]">
              Un métier
              <br />
              <span className="text-[#F08722]">de précision.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-[#E6E6E0] max-w-lg">
              Un sol bien fait ne se voit pas : il se sent. Planéité, niveau,
              dosage, temps de séchage — chaque paramètre conditionne la
              qualité finale du revêtement posé par-dessus. Chez Euro Chape,
              rien n'est laissé au hasard.
            </p>
            <Link
              to="/a-propos"
              data-testid="home-about-cta"
              className="mt-10 inline-flex items-center gap-3 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#F08722] hover:border-[#F08722] transition-colors w-fit"
            >
              En savoir plus <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NOS SOLUTIONS — pictogram grid */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <SectionLabel className="justify-center inline-flex">
              Nos solutions
            </SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#1A2D7E]">
              Des réponses
              <br />
              <span className="text-[#F08722]">à vos contraintes.</span>
            </h2>
            <p className="mt-8 text-base sm:text-lg font-light text-[#1A2D7E]">
              Calendrier serré, rénovation complexe, local à fort passage :
              chaque chantier a ses spécificités. Nos solutions techniques
              s'adaptent au besoin réel plutôt qu'à une recette unique.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D5D5D0] border border-[#D5D5D0] mt-16">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <Link
                to={s.to}
                data-testid={`home-solution-${i}`}
                className="block bg-[#F4F4F0] p-8 md:p-10 hover:bg-[#1A2D7E] hover:text-white transition-colors group h-full"
              >
                <div className="flex items-start justify-between">
                  <s.icon className="w-9 h-9 text-[#F08722]" strokeWidth={1.5} />
                  <ArrowUpRight className="w-5 h-5 text-[#1A2D7E] group-hover:text-white" />
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl mt-10">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm font-light text-[#5C5E58] group-hover:text-[#E6E6E0] leading-relaxed">
                  {s.desc}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTACT BAR */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 bg-[#EBEBE6] border-y border-[#D5D5D0]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          <ContactItem icon={MapPin} label="Adresse" value="1 rue de Habsheim, 68110 Illzach" />
          <ContactItem
            icon={Phone}
            label="Téléphone"
            value="07 67 94 54 59"
            href="tel:+33767945459"
            testid="trust-phone"
          />
          <ContactItem
            icon={Mail}
            label="Email"
            value="eurochape68@gmail.com"
            href="mailto:eurochape68@gmail.com"
            testid="trust-email"
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center bg-[#1A2D7E] text-white">
        <Reveal>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Prêt à couler
            <br />
            <span className="text-[#F08722]">vos sols ?</span>
          </h2>
          <Link
            to="/devis"
            data-testid="home-final-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#F08722] text-white px-10 py-5 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-[#1A2D7E] transition-colors"
          >
            Demander un devis gratuit <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const content = (
    <>
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#5C5E58] font-semibold">
        <Icon className="w-4 h-4 text-[#F08722]" />
        {label}
      </div>
      <div className="font-display font-bold text-lg md:text-xl mt-3 tracking-tight text-[#1A2D7E]">
        {value}
      </div>
    </>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block link-underline">
      {content}
    </a>
  ) : (
    <div data-testid={testid}>{content}</div>
  );
}
